import { ref, watchEffect } from "vue";
import { ManifestParser } from "@/lib/manifest_parser.js";

/**
 * Vue Composable: IIIF Manifest Data Management
 *
 * This composable handles all aspects of IIIF manifest data parsing and management.
 * It provides reactive access to manifest data, manages the currently active item,
 * and synchronizes state with external viewer components.
 *
 * Features:
 * - Automatic manifest parsing from DOM
 * - Active item lifecycle management
 * - Viewer state synchronization
 * - Reactive thumbnail and title extraction
 *
 * @returns {Object} Manifest data and active item management functions
 */
export function useManifestData() {
    // MANIFEST PARSING

    /**
     * Parse IIIF manifest from DOM element
     *
     * The ManifestParser extracts manifest data from a DOM script element
     * and provides convenient access to common properties like thumbnail and title.
     */
    const { manifest, thumbnail, title } = new ManifestParser();

    // ACTIVE ITEM STATE

    /**
     * Currently selected manifest item
     *
     * Represents the active canvas/page being viewed in the IIIF viewer.
     * This is synchronized between the internal state and the viewer component.
     *
     * @type {import('vue').Ref<Object|null>}
     */
    const activeItem = ref(null);

    /**
     * Auto-select first manifest item as fallback
     *
     * This watcher ensures that if no active item is selected and the manifest
     * contains items, the first item is automatically selected. This provides
     * a sensible default behavior when the viewer is initialized.
     */
    watchEffect(() => {
        if (!activeItem.value && manifest?.items?.length) {
            activeItem.value = manifest.items[0];
        }
    });

    // SYNCHRONIZATION METHODS

    /**
     * Update active item from external viewer component
     *
     * Synchronizes the active item state with the Glycerine viewer component.
     * The viewer may change the active item through user interaction (navigation),
     * and this method ensures our local state stays in sync.
     *
     * @param {import('vue').Ref} viewerRef - Reference to the viewer component
     */
    function updateActiveItemFromViewer(viewerRef) {
        if (viewerRef?.value) {
            // Prefer viewer's active item, fallback to current value
            activeItem.value =
                viewerRef.value.activeIndexItem ?? activeItem.value;
        }
    }

    // PUBLIC API

    return {
        // IIIF Manifest Data
        manifest,
        thumbnail,
        title,

        // Active Item Management
        activeItem,

        // Synchronization Methods
        updateActiveItemFromViewer,
    };
}
